import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-transparent bg-clip-padding text-sm font-medium shadow-[0_1px_2px_var(--shadow-soft)] transition-all active:not-disabled:translate-y-px focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        primary:
          "bg-[var(--color-brand)] text-white hover:bg-[color-mix(in_srgb,var(--color-brand)_86%,var(--color-accent))] focus-visible:outline-[var(--color-focus)]",
        secondary:
          "border-[var(--color-border)] bg-[var(--color-surface)] text-[var(--color-brand)] hover:border-[var(--color-accent)] hover:bg-[var(--color-brand-soft)] focus-visible:outline-[var(--color-focus)]",
        ghost:
          "shadow-none text-[var(--color-foreground-muted)] hover:bg-[var(--color-surface-muted)] hover:text-[var(--color-foreground)] focus-visible:outline-[var(--color-focus)]",
        danger:
          "bg-[color-mix(in_srgb,var(--color-danger-fill)_12%,white)] text-[var(--color-danger)] hover:bg-[color-mix(in_srgb,var(--color-danger-fill)_18%,white)] focus-visible:outline-[var(--color-danger)]",
        link:
          "h-auto rounded-none p-0 shadow-none text-[var(--color-brand)] underline-offset-4 hover:underline focus-visible:outline-[var(--color-focus)]",
      },
      size: {
        xs: "h-7 gap-1 px-2 text-xs [&_svg:not([class*='size-'])]:size-3",
        sm: "h-8 gap-1.5 px-3 [&_svg:not([class*='size-'])]:size-3.5",
        md: "h-9 px-3.5 [&_svg:not([class*='size-'])]:size-4",
        lg: "h-10 px-4 [&_svg:not([class*='size-'])]:size-4",
        icon: "size-9 p-0 [&_svg:not([class*='size-'])]:size-4",
      },
    },
    defaultVariants: {
      variant: "primary",
      size: "md",
    },
  },
);

export type ButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> &
  VariantProps<typeof buttonVariants>;

export function Button({ className, size, variant, ...props }: ButtonProps) {
  return (
    <button
      className={cn(buttonVariants({ size, variant }), className)}
      type="button"
      {...props}
    />
  );
}

export { buttonVariants };
